import React, { useState } from "react";
import { useTrapsSelectionStore } from "../../stores/trapsSelectionStore";
import { useTrapSkuStore } from "../../stores/trapSkuStore";
import { useConfirmationStore } from "components";
import { PRESENCE_OPTIONS } from "../../utils/trapSkuConfig";

// Компонент для відображення кнопок для режиму вибору
const TrapsBulkActions = ({ currentSkusOnPage, isEmpty }) => {
  // Отримуємо дані та дії зі стору виділення
  const isSelectionModeActive = useTrapsSelectionStore(
    (state) => state.isSelectionModeActive,
  );
  const selectedSkus = useTrapsSelectionStore((state) => state.selectedSkus);
  const toggleSelectionMode = useTrapsSelectionStore(
    (state) => state.toggleSelectionMode,
  );
  const selectAll = useTrapsSelectionStore((state) => state.selectAll);
  const updatePresenceForSelected = useTrapsSelectionStore(
    (state) => state.updatePresenceForSelected,
  );
  const isSelectionLoading = useTrapsSelectionStore((state) => state.isLoading);

  // Отримуємо дії зі стору ловушок
  const fetchTrapSkus = useTrapSkuStore((state) => state.fetchTrapSkus);
  const massUpdateCustomPrices = useTrapSkuStore(
    (state) => state.massUpdateCustomPrices,
  );
  const massResetCustomPrices = useTrapSkuStore((state) => state.massResetCustomPrices);
  const isGlobalLoading = useTrapSkuStore((state) => state.isLoading);

  // Отримуємо хук для відображення підтвердження
  const showConfirmation = useConfirmationStore((state) => state.showConfirmation);

  const isLoading = isSelectionLoading || isGlobalLoading;

  // Локальні стани керування вибором дії
  const [actionType, setActionType] = useState("presence"); // "presence", "markup", "reset"

  // Стани для наявності
  const [selectedStatus, setSelectedStatus] = useState("available");

  // Стани для кастомної ціни
  const [markupType, setMarkupType] = useState("percentage"); // "percentage", "fixed"
  const [markupValue, setMarkupValue] = useState("");

  // Валідація кнопки застосування
  // Дозволяємо від'ємні значення (зниження ціни), блокуємо лише порожнє/нечислове/нуль
  const isApplyDisabled =
    isLoading ||
    selectedSkus.length === 0 ||
    (actionType === "markup" &&
      (markupValue === "" ||
        isNaN(Number(markupValue)) ||
        Number(markupValue) === 0));

  // Обробник оновлення
  const handleUpdate = async () => {
    if (actionType === "presence") {
      showConfirmation({
        title: "Зміна статусу",
        message: `Ви впевнені, що хочете встановити статус "${selectedStatus}" для ${selectedSkus.length} товарів?`,
        onConfirm: async () => {
          await updatePresenceForSelected(selectedStatus);
          fetchTrapSkus();
        },
      });
    } else if (actionType === "markup") {
      const val = Number(markupValue);
      const typeLabel = markupType === "percentage" ? "%" : "грн";
      const actionLabel = val < 0 ? "знизити ціну на" : "додати націнку";
      showConfirmation({
        title: "Зміна ціни",
        message: `Ви впевнені, що хочете ${actionLabel} ${Math.abs(val)}${typeLabel} для ${selectedSkus.length} товарів?`,
        onConfirm: async () => {
          await massUpdateCustomPrices(selectedSkus, markupType, val);
          toggleSelectionMode(); // Вимикаємо режим вибору після успішного збереження
        },
      });
    } else if (actionType === "reset") {
      showConfirmation({
        title: "Скидання націнок",
        message: `Ви впевнені, що хочете скинути кастомні ціни для ${selectedSkus.length} товарів? Вони повернуться до базових значень.`,
        onConfirm: async () => {
          await massResetCustomPrices(selectedSkus);
          toggleSelectionMode(); // Вимикаємо режим вибору після успішного збереження
        },
      });
    }
  };

  // Режим вибору неактивний, показуємо кнопку включення
  if (!isSelectionModeActive) {
    return (
      <button
        className="focus-button"
        onClick={toggleSelectionMode}
        title="Увімкнути масове редагування"
        disabled={isEmpty}
      >
        Редагувати
      </button>
    );
  }

  return (
    <div className="bulk-bar">
      {/* Інфо */}
      <span className="bulk-bar__info">Обрано: {selectedSkus.length}</span>

      {/* Всі */}
      <button className="bulk-bar__link" onClick={() => selectAll(currentSkusOnPage)}>
        Обрати Всі
      </button>

      {/* Тип дії */}
      <select
        className="form-select"
        value={actionType}
        onChange={(e) => setActionType(e.target.value)}
        disabled={isLoading}
      >
        <option value="presence">Наявність</option>
        <option value="markup">Націнка (Кастомна ціна)</option>
        <option value="reset">Скинути націнку</option>
      </select>

      {/* Динамічні поля залежно від дії */}
      {actionType === "presence" && (
        <select
          className="form-select"
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          disabled={isLoading}
        >
          {PRESENCE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )}

      {actionType === "markup" && (
        <div style={{ display: "flex", gap: "10px" }}>
          <select
            className="form-select"
            value={markupType}
            onChange={(e) => setMarkupType(e.target.value)}
            disabled={isLoading}
          >
            <option value="percentage">Відсоток (%)</option>
            <option value="fixed">Сума (грн)</option>
          </select>
          <input
            type="number"
            className="form-input"
            value={markupValue}
            onChange={(e) => setMarkupValue(e.target.value)}
            placeholder="0"
            step="0.1"
            disabled={isLoading}
          />
        </div>
      )}

      {/* Дії */}
      <button
        className="focus-button"
        onClick={handleUpdate}
        disabled={isApplyDisabled}
      >
        {isLoading ? "Завантаження" : "Застосувати"}
      </button>

      <button
        className="focus-button-secondary"
        onClick={toggleSelectionMode}
        disabled={isLoading}
      >
        Скасувати
      </button>
    </div>
  );
};

export default TrapsBulkActions;
